import React from "react";
import BaseCard from "../../../components/ui/BaseCard";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import { useFormik } from "formik";
import useAuth from "../../../hooks/useAuth";
import { useParams } from "react-router-dom";
import {
  createDesignationPermissions,
  fetchAllPermissionsByDesignation,
} from "../../../services/api/designation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ModuleType } from "../../../utils/enum";
import { getDesignationPermissionsByModule } from "../../../utils/getDesignationPermissionsByModule";

const WorkBoard = ({
  designationPermissions,
  designationId,
  refetchAllPermissionsByDesignation,
}) => {
  const workOrderPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.WORK_ORDERS
  );
  const workOrderApproveActionPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.ACTION_APPROVE_WORK_ORDER
  );
  const workOrderVerifyActionPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.ACTION_VERIFY_WORK_ORDER
  );
  const workOrderReviewActionPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.ACTION_REVIEW_WORK_ORDER
  );
  const workOrderCancelActionPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.ACTION_CANCEL_WORK_ORDER
  );

  const customerWorkOrderPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.CUSTOMER_WORK_ORDERS
  );

  const clientPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.CLIENT_CUSTOMER
  );

  const projectPermissions = getDesignationPermissionsByModule(
    designationPermissions,
    ModuleType.CLIENT_PROJECT
  );

  const {
    isPending: isCreateDesignationPermissionsApiLoading,
    error: isCreateDesignationPermissionsApiError,

    mutateAsync: createDesignationPermissionsMutation,
  } = useMutation({
    mutationFn: createDesignationPermissions,
    enabled: false,
  });

  const formik = useFormik({
    initialValues: {
      WORK_ORDERS: {
        isRead: workOrderPermissions.hasReadAccess,
        isWrite: workOrderPermissions.hasWriteAccess,
        isUpdate: workOrderPermissions.hasUpdateAccess,
        isDelete: workOrderPermissions.hasDeleteAccess,
      },
      CLIENT_CUSTOMER: {
        isRead: clientPermissions.hasReadAccess,
        isWrite: clientPermissions.hasWriteAccess,
        isUpdate: clientPermissions.hasUpdateAccess,
        isDelete: clientPermissions.hasDeleteAccess,
      },
      CLIENT_PROJECT: {
        isRead: projectPermissions.hasReadAccess,
        isWrite: projectPermissions.hasWriteAccess,
        isUpdate: projectPermissions.hasUpdateAccess,
        isDelete: projectPermissions.hasDeleteAccess,
      },
      ACTION_APPROVE_WORK_ORDER: {
        isWrite: workOrderApproveActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },

      ACTION_VERIFY_WORK_ORDER: {
        isWrite: workOrderVerifyActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_REVIEW_WORK_ORDER: {
        isWrite: workOrderReviewActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      ACTION_CANCEL_WORK_ORDER: {
        isWrite: workOrderCancelActionPermissions.hasWriteAccess,
        isRead: false,
        isUpdate: false,
        isDelete: false,
      },
      CUSTOMER_WORK_ORDERS: {
        isRead: customerWorkOrderPermissions.hasReadAccess,
        isWrite: false,
        isUpdate: false,
        isDelete: false,
      },
    },
    onSubmit: (values) => {
      // Replace with your actual submit handler
      // handleConfirmApprovalButton(values);
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnMount: true,
  });

  const handleWorkOrderPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.WORK_ORDERS,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleCancelWorkOrderPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_CANCEL_WORK_ORDER,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleVerifyWorkOrderPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_VERIFY_WORK_ORDER,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleReviewWorkOrderPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_REVIEW_WORK_ORDER,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleApproveWorkOrderPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.ACTION_APPROVE_WORK_ORDER,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleCustomerWorkOrderPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.CUSTOMER_WORK_ORDERS,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleClientPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.CLIENT_CUSTOMER,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  const handleProjectPermissionsChange = async (
    e,
    value,
    module,
    permissionId
  ) => {
    try {
      formik.setFieldValue(e.target.name, e.target.checked);
      const payload = {
        ...formik.values.CLIENT_PROJECT,
        [value]: e.target.checked,
        module,
        designationId: +designationId,
        permissionId,
      };

      await createDesignationPermissionsMutation(payload);
      await refetchAllPermissionsByDesignation();
    } catch (error) {}
  };

  return (
    <BaseCard elevation={0} className="p-5 rounded-lg">
      <div className="border-b pb-4">
        <p className="text-dark text-base font-normal mb-2">Work Order</p>
        <div className="grid grid-cols-8">
          <BaseCheckbox
            label="Write Work Order"
            name="WORK_ORDERS.isRead"
            checked={formik.values.WORK_ORDERS.isRead}
            onChange={(e) =>
              handleWorkOrderPermissionsChange(
                e,
                "isRead",
                ModuleType.WORK_ORDERS,
                workOrderPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Create Work Order"
            name="WORK_ORDERS.isWrite"
            checked={formik.values.WORK_ORDERS.isWrite}
            onChange={(e) =>
              handleWorkOrderPermissionsChange(
                e,
                "isWrite",
                ModuleType.WORK_ORDERS,
                workOrderPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Edit Work Order"
            name="WORK_ORDERS.isUpdate"
            checked={formik.values.WORK_ORDERS.isUpdate}
            onChange={(e) =>
              handleWorkOrderPermissionsChange(
                e,
                "isUpdate",
                ModuleType.WORK_ORDERS,
                workOrderPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Delete Work Order"
            name="WORK_ORDERS.isDelete"
            checked={formik.values.WORK_ORDERS.isDelete}
            onChange={(e) =>
              handleWorkOrderPermissionsChange(
                e,
                "isDelete",
                ModuleType.WORK_ORDERS,
                workOrderPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Approve Work Order"
            name="ACTION_APPROVE_WORK_ORDER.isWrite"
            checked={formik.values.ACTION_APPROVE_WORK_ORDER?.isWrite}
            onChange={(e) =>
              handleApproveWorkOrderPermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_APPROVE_WORK_ORDER,
                workOrderApproveActionPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Verify Work Order"
            name="ACTION_VERIFY_WORK_ORDER.isWrite"
            checked={formik.values.ACTION_VERIFY_WORK_ORDER?.isWrite}
            onChange={(e) =>
              handleVerifyWorkOrderPermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_VERIFY_WORK_ORDER,
                workOrderVerifyActionPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Review Work Order"
            name="ACTION_REVIEW_WORK_ORDER.isWrite"
            checked={formik.values.ACTION_REVIEW_WORK_ORDER?.isWrite}
            onChange={(e) =>
              handleReviewWorkOrderPermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_REVIEW_WORK_ORDER,
                workOrderReviewActionPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Cancel Work Order"
            name="ACTION_CANCEL_WORK_ORDER.isWrite"
            checked={formik.values.ACTION_CANCEL_WORK_ORDER?.isWrite}
            onChange={(e) =>
              handleCancelWorkOrderPermissionsChange(
                e,
                "isWrite",
                ModuleType.ACTION_CANCEL_WORK_ORDER,
                workOrderCancelActionPermissions.permissionId
              )
            }
          />
        </div>
      </div>
      <div className="border-b pb-4 mt-2">
        <p className="text-dark text-base font-normal mb-2">
          Customer Work Order
        </p>
        <div className="grid grid-cols-8">
          <BaseCheckbox
            label="Read Customer Work Order"
            name="CUSTOMER_WORK_ORDERS.isRead"
            checked={formik.values.CUSTOMER_WORK_ORDERS.isRead}
            onChange={(e) =>
              handleCustomerWorkOrderPermissionsChange(
                e,
                "isRead",
                ModuleType.CUSTOMER_WORK_ORDERS,
                customerWorkOrderPermissions.permissionId
              )
            }
          />
        </div>
      </div>

      <div className="border-b pb-4 mt-2">
        <p className="text-dark text-base font-normal mb-2">Clients</p>
        <div className="grid grid-cols-8">
          <BaseCheckbox
            label="Read Client"
            name="CLIENT_CUSTOMER.isRead"
            checked={formik.values.CLIENT_CUSTOMER.isRead}
            onChange={(e) =>
              handleClientPermissionsChange(
                e,
                "isRead",
                ModuleType.CLIENT_CUSTOMER,
                clientPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Create Client"
            name="CLIENT_CUSTOMER.isWrite"
            checked={formik.values.CLIENT_CUSTOMER.isWrite}
            onChange={(e) =>
              handleClientPermissionsChange(
                e,
                "isWrite",
                ModuleType.CLIENT_CUSTOMER,
                clientPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Edit Client"
            name="CLIENT_CUSTOMER.isUpdate"
            checked={formik.values.CLIENT_CUSTOMER.isUpdate}
            onChange={(e) =>
              handleClientPermissionsChange(
                e,
                "isUpdate",
                ModuleType.CLIENT_CUSTOMER,
                clientPermissions.permissionId
              )
            }
          />

          <BaseCheckbox
            label="Delete Client"
            name="CLIENT_CUSTOMER.isDelete"
            checked={formik.values.CLIENT_CUSTOMER.isDelete}
            onChange={(e) =>
              handleClientPermissionsChange(
                e,
                "isDelete",
                ModuleType.CLIENT_CUSTOMER,
                clientPermissions.permissionId
              )
            }
          />
        </div>
      </div>
      <div className="border-b pb-4 mt-2">
        <p className="text-dark text-base font-normal mb-2">Projects</p>
        <div className="grid grid-cols-8">
          <BaseCheckbox
            label="Read Project"
            name="CLIENT_PROJECT.isRead"
            checked={formik.values.CLIENT_PROJECT.isRead}
            onChange={(e) =>
              handleProjectPermissionsChange(
                e,
                "isRead",
                ModuleType.CLIENT_PROJECT,
                projectPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Create Project"
            name="CLIENT_PROJECT.isWrite"
            checked={formik.values.CLIENT_PROJECT.isWrite}
            onChange={(e) =>
              handleProjectPermissionsChange(
                e,
                "isWrite",
                ModuleType.CLIENT_PROJECT,
                projectPermissions.permissionId
              )
            }
          />
          <BaseCheckbox
            label="Edit Project"
            name="CLIENT_PROJECT.isUpdate"
            checked={formik.values.CLIENT_PROJECT.isUpdate}
            onChange={(e) =>
              handleProjectPermissionsChange(
                e,
                "isUpdate",
                ModuleType.CLIENT_PROJECT,
                projectPermissions.permissionId
              )
            }
          />

          <BaseCheckbox
            label="Delete Project"
            name="CLIENT_PROJECT.isDelete"
            checked={formik.values.CLIENT_PROJECT.isDelete}
            onChange={(e) =>
              handleProjectPermissionsChange(
                e,
                "isDelete",
                ModuleType.CLIENT_PROJECT,
                projectPermissions.permissionId
              )
            }
          />
        </div>
      </div>
    </BaseCard>
  );
};

export default WorkBoard;
